/**
 * Matching a reference to a Crossref record, and writing the DOI back in APA 7 form.
 *
 * Crossref's `query.bibliographic` takes a whole reference as typed and returns its
 * best guesses, but its relevance score is relative to each query — it says which
 * candidate is likeliest, not whether any is right. So each candidate is checked
 * against the reference on three things a reader would check: are the words of its
 * title in the reference, is its first author's surname near the start, and does its
 * year appear. Only a candidate that passes all three counts as confident.
 */

import { findDoi } from "@/lib/tool-kit/reference-tools/references";

export interface CrossrefCandidate {
  doi: string;
  title: string;
  /** Surnames (or an organisation's name), in order. */
  authors: string[];
  year: number | null;
  container: string;
  type: string;
}

export type YearMatch = "exact" | "near" | "no" | null;

export interface MatchScore {
  /** Share of the candidate title's words found in the reference, 0–1. */
  title: number;
  /** How many significant words the candidate title has. */
  titleWords: number;
  /** First author's surname near the start of the reference; null if no author. */
  author: boolean | null;
  /** The candidate's year in the reference; "near" is one year off (online first). */
  year: YearMatch;
}

export type MatchLevel = "confident" | "uncertain" | "none";

export const CROSSREF_ROWS = 3;
const CROSSREF_SELECT =
  "DOI,title,author,issued,published-print,published-online,container-title,type";
const MAX_QUERY = 600;
/** How far into the reference the first author's surname may sit. */
const AUTHOR_ZONE = 140;

const RE_LIST_MARKER = /^\s*(?:\[\d{1,4}\]|\d{1,4}[.)])\s+/;
const RE_URL = /\b(?:https?:\/\/|www\.)\S+/gi;
const RE_SPACES = /\s+/g;
const RE_TAGS = /<[^>]+>/g;
const RE_DIACRITICS = /\p{M}/gu;
const RE_NOT_ALNUM = /[^\p{L}\p{N}]+/gu;
const RE_YEAR = /\b(1[5-9]\d{2}|20\d{2})\b/g;
const RE_SENTENCE_END = /[.!?]$/;
const RE_TRAILING_LINK =
  /(?:\.?\s*(?:Retrieved from|Available at:?|Available from:?)\s*)?\s*(?:https?:\/\/|www\.)\S+$/i;
const RE_UNSAFE_URL = /[#?%"<>\s]/g;
const RE_TITLE_SPLIT = /\s*[:–—]\s+|\s+-\s+/;

/**
 * Words that mark a record about another record — a reply, a correction, a
 * retraction notice, a recommendation — which shares the original's title words.
 */
const NOTICE_WORDS = new Set([
  "reply",
  "response",
  "correction",
  "corrections",
  "corrigendum",
  "erratum",
  "errata",
  "retraction",
  "retracted",
  "withdrawn",
  "withdrawal",
  "comment",
  "comments",
  "commentary",
  "recommendation",
]);
/** A notice's title counts for this much of its word overlap. */
const NOTICE_PENALTY = 0.6;
/** Share of a title's words that must appear in the reference to be worth showing. */
const UNCERTAIN_TITLE = 2 / 3;
/** …and to count as the same work. */
const CONFIDENT_TITLE = 0.9;

const STOPWORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "by",
  "for",
  "from",
  "in",
  "into",
  "is",
  "its",
  "of",
  "on",
  "or",
  "the",
  "to",
  "with",
]);

/* --------------------------------------------------------------- text */

function fold(text: string): string {
  return text
    .normalize("NFKD")
    .replace(RE_DIACRITICS, "")
    .toLowerCase()
    .replace(RE_NOT_ALNUM, " ")
    .trim();
}

function words(text: string): string[] {
  const folded = fold(text);
  return folded === "" ? [] : folded.split(" ");
}

function significant(tokens: string[]): string[] {
  const kept = tokens.filter((token) => !STOPWORDS.has(token));
  return kept.length >= 2 ? kept : tokens;
}

/** The reference as sent to Crossref: no list number, no links, one line. */
export function queryText(reference: string): string {
  return reference
    .replace(RE_LIST_MARKER, "")
    .replace(RE_URL, " ")
    .replace(RE_SPACES, " ")
    .trim()
    .slice(0, MAX_QUERY);
}

export function crossrefQueryUrl(reference: string): string {
  const query = encodeURIComponent(queryText(reference));
  return `https://api.crossref.org/works?query.bibliographic=${query}&rows=${CROSSREF_ROWS}&select=${CROSSREF_SELECT}`;
}

/* ------------------------------------------------------------- parsing */

interface RawCrossrefItem {
  DOI?: unknown;
  title?: unknown;
  author?: unknown;
  issued?: unknown;
  "published-print"?: unknown;
  "published-online"?: unknown;
  "container-title"?: unknown;
  type?: unknown;
}

function firstString(value: unknown): string {
  if (Array.isArray(value)) {
    return typeof value[0] === "string" ? value[0] : "";
  }
  return typeof value === "string" ? value : "";
}

function yearOf(...dates: unknown[]): number | null {
  for (const date of dates) {
    const parts = (date as { "date-parts"?: unknown[][] } | undefined)?.[
      "date-parts"
    ]?.[0];
    const year = Array.isArray(parts) ? Number(parts[0]) : Number.NaN;
    if (Number.isInteger(year) && year > 0) {
      return year;
    }
  }
  return null;
}

function authorNames(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((person) => {
    const record = person as { family?: unknown; name?: unknown };
    const name =
      typeof record.family === "string" ? record.family : record.name;
    return typeof name === "string" && name.trim() !== "" ? [name.trim()] : [];
  });
}

/** Candidates from a Crossref `/works` response; anything malformed is skipped. */
export function parseCrossrefItems(body: unknown): CrossrefCandidate[] {
  const items = (body as { message?: { items?: unknown } } | null)?.message
    ?.items;
  if (!Array.isArray(items)) {
    return [];
  }
  return items.flatMap((raw) => {
    const item = raw as RawCrossrefItem;
    const doi = typeof item.DOI === "string" ? item.DOI.trim() : "";
    if (doi === "") {
      return [];
    }
    return [
      {
        doi,
        title: firstString(item.title).replace(RE_TAGS, "").trim(),
        authors: authorNames(item.author),
        year: yearOf(
          item.issued,
          item["published-print"],
          item["published-online"]
        ),
        container: firstString(item["container-title"])
          .replace(RE_TAGS, "")
          .trim(),
        type: typeof item.type === "string" ? item.type : "",
      },
    ];
  });
}

/* ------------------------------------------------------------- scoring */

function containment(title: string, reference: Set<string>): number {
  const tokens = significant(words(title));
  if (tokens.length === 0) {
    return 0;
  }
  return tokens.filter((token) => reference.has(token)).length / tokens.length;
}

function yearMatch(candidate: number | null, reference: string): YearMatch {
  const years = [...reference.matchAll(RE_YEAR)].map((match) =>
    Number(match[1])
  );
  if (candidate === null || years.length === 0) {
    return null;
  }
  if (years.includes(candidate)) {
    return "exact";
  }
  return years.some((year) => Math.abs(year - candidate) === 1) ? "near" : "no";
}

function authorMatch(candidate: CrossrefCandidate, reference: string) {
  const first = candidate.authors[0];
  if (!first) {
    return null;
  }
  const zone = new Set(words(queryText(reference).slice(0, AUTHOR_ZONE)));
  const name = words(first);
  return name.length > 0 && name.every((part) => zone.has(part));
}

/** The candidate's title has a notice word the reference does not. */
function isNotice(title: string, reference: Set<string>): boolean {
  return words(title).some(
    (word) => NOTICE_WORDS.has(word) && !reference.has(word)
  );
}

export function scoreCandidate(
  reference: string,
  candidate: CrossrefCandidate
): MatchScore {
  const tokens = new Set(words(queryText(reference)));
  // A reference may drop the subtitle the record carries, so the main title alone
  // counts too — at a small discount, since it is less to go on.
  const main = candidate.title.split(RE_TITLE_SPLIT)[0] ?? "";
  const full = containment(candidate.title, tokens);
  const mainOnly =
    main === candidate.title ? 0 : containment(main, tokens) * 0.95;
  const penalty = isNotice(candidate.title, tokens) ? NOTICE_PENALTY : 1;
  const titleScore = Math.max(full, mainOnly) * penalty;
  const titleWords = significant(
    words(full >= mainOnly ? candidate.title : main)
  ).length;
  return {
    title: titleScore,
    titleWords,
    author: authorMatch(candidate, reference),
    year: yearMatch(candidate.year, queryText(reference)),
  };
}

export function judgeMatch(score: MatchScore): MatchLevel {
  const authorOk = score.author !== false;
  const yearOk = score.year !== "no";
  const backedUp = score.author === true && score.year === "exact";
  const strongTitle =
    score.title >= CONFIDENT_TITLE && (score.titleWords >= 4 || backedUp);
  if (strongTitle && authorOk && yearOk) {
    return "confident";
  }
  return score.title >= UNCERTAIN_TITLE ? "uncertain" : "none";
}

function rankValue(score: MatchScore): number {
  let value = score.title * 3;
  if (score.author === true) {
    value += 1;
  }
  if (score.year === "exact") {
    value += 1;
  } else if (score.year === "near") {
    value += 0.5;
  }
  return value;
}

export interface BestMatch {
  candidate: CrossrefCandidate;
  score: MatchScore;
  level: MatchLevel;
}

/** The candidate that best fits the reference, or null when none is close. */
export function bestMatch(
  reference: string,
  candidates: readonly CrossrefCandidate[]
): BestMatch | null {
  let best: BestMatch | null = null;
  let bestValue = -1;
  for (const candidate of candidates) {
    const score = scoreCandidate(reference, candidate);
    const value = rankValue(score);
    if (value > bestValue) {
      bestValue = value;
      best = { candidate, score, level: judgeMatch(score) };
    }
  }
  return best && best.level !== "none" ? best : null;
}

/* -------------------------------------------------------------- output */

/** A reference that already carries a DOI is left alone. */
export function hasDoi(reference: string): boolean {
  return findDoi(reference) !== null;
}

/** Too short to be a reference worth sending: fewer than four words. */
export function tooShort(reference: string): boolean {
  return words(queryText(reference)).length < 4;
}

/** A DOI as an https://doi.org link, escaping the few characters a URL cannot hold. */
export function doiUrl(doi: string): string {
  return `https://doi.org/${doi.replace(
    RE_UNSAFE_URL,
    (char) =>
      `%${char.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`
  )}`;
}

/**
 * The reference with its DOI at the end, in APA 7 form: after the closing full stop,
 * with no full stop after it. APA 7 gives the DOI instead of a database link, so a
 * trailing URL (and a "Retrieved from" before it) is replaced.
 */
export function withDoi(
  reference: string,
  doi: string
): { text: string; replacedLink: boolean } {
  const trimmed = reference.trimEnd();
  const withoutLink = trimmed.replace(RE_TRAILING_LINK, "").trimEnd();
  const replacedLink = withoutLink !== trimmed;
  const end = RE_SENTENCE_END.test(withoutLink) ? "" : ".";
  return { text: `${withoutLink}${end} ${doiUrl(doi)}`, replacedLink };
}
